import dayjs, { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import * as yup from 'yup'
import type { CustomerProductFormMode } from './CustomerProductForm.interface'

dayjs.extend(utc)

const isValidDate = (value: unknown): boolean => {
  return dayjs.isDayjs(value) && (value as Dayjs).isValid()
}

/**
 * Reads a picker value as the wall clock the admin sees, independent of whether MUI handed us
 * a UTC-mode or a local-mode `Dayjs`. Editing a row that already has an end time seeds a
 * UTC-mode value; editing an Ongoing row seeds `null`, so a freshly picked value is
 * local-mode. Comparing those two as instants is what made this validator disagree with the
 * backend, which compares the stored digits against UTC now.
 */
const toWallClock = (value: Dayjs): Dayjs => {
  return dayjs.utc(value.format('YYYY-MM-DD HH:mm:ss'))
}

/**
 * A factory rather than a constant: the two modes have genuinely different rules, and both of
 * the edit-mode rules mirror 400s that `PartnerProductService.update` raises — validating them
 * here turns a round trip into an inline field error.
 */
export const buildCustomerProductFormValidator = (mode: CustomerProductFormMode) => {
  const isEdit = mode === 'edit'

  return yup.object({
    // On edit the product is read-only and the API takes only `productId` + `endTime`, so it
    // is not validated in that mode.
    // `.nullable()` here (and below): yup's `mixed()` rejects `null` at the type-check stage
    // before any `.test()` runs, so an unvalidated field must still declare itself nullable
    // or a `null` value throws a generic "cannot be null" error instead of passing through.
    product: isEdit ? yup.mixed().nullable() : yup.string().required('Product is required'),

    startTime: isEdit
      ? yup.mixed().nullable()
      : yup
          .mixed()
          .nullable()
          .required('Start time is required')
          .test('is-valid-start', 'Start time is not a valid date', (value) => {
            return isValidDate(value)
          })
          // The picker also sets `minDate`, but that only greys out the calendar — a typed
          // date bypasses it entirely, so the floor has to live here too. Compared as a wall
          // clock against the start of today in UTC, matching the "(UTC)" field labels: an
          // entitlement is granted from now on, never backdated.
          .test('start-not-before-today', 'Start date cannot be in the past', (value) => {
            if (!isValidDate(value)) {
              return true
            }

            return !toWallClock(value as Dayjs).isBefore(dayjs.utc().startOf('day'))
          }),

    endTime: yup
      .mixed()
      .nullable()
      .test('end-required-on-edit', 'End time is required', (value) => {
        if (!isEdit) {
          return true
        }

        return isValidDate(value)
      })
      // Mirrors 'End time should be greater than start time'.
      .test('end-after-start', 'End time must be after the start time', (value, context) => {
        if (!isValidDate(value)) {
          return true
        }

        const startTime = context.parent.startTime

        if (!isValidDate(startTime)) {
          return true
        }

        return toWallClock(value as Dayjs).isAfter(toWallClock(startTime as Dayjs))
      })
      // Mirrors 'End time should be in the future'. Only on edit: the create route has no such
      // rule, and a create with a past end time is a legitimate backdated entitlement.
      .test('end-in-future-on-edit', 'End time must be in the future', (value) => {
        if (!isEdit || !isValidDate(value)) {
          return true
        }

        // Against UTC now, because the digits the admin typed are stored verbatim and the
        // backend compares them to its own UTC clock. Comparing to local `dayjs()` opens a
        // whole-offset window of end times this accepts and the backend 400s.
        return toWallClock(value as Dayjs).isAfter(dayjs.utc())
      }),
  })
}
